/**
 * Translations for the public tool funnel pages (/tools, /tools/:slug).
 *
 * Strategy: we hand-write SK + EN as ground truth (most-converting markets),
 * and rely on the existing language-switcher to fall back to EN for any
 * locale we haven't shipped yet. Backend will lazily AI-translate the rest
 * via Emergent LLM key on the first hit per language and cache in Mongo
 * (next agent task — for now SK/EN cover ~80% of traffic).
 */

const HUB_SK = {
  hero_eyebrow: "BEZPLATNÉ AI NÁSTROJE",
  hero_h1: "6 AI nástrojov ktoré nahradia agentúru za €3 000/mes",
  hero_sub: "Vlastné AI agenty. Žiadny Ahrefs, žiadny SEMrush. Sken za 30 sekúnd. Bez registrácie.",
  cta_run: "Spustiť",
  card_status_live: "DOSTUPNÉ",
  card_status_soon: "ČOSKORO",
  wait_cta: "Upozorniť ma pri spustení",
  wait_title: "Upozorniť ma keď spustíme",
  wait_sub: "Napíš email a dáme ti vedieť presne v deň spustenia. Žiadny spam.",
  wait_placeholder: "tvoj@email.sk",
  wait_btn: "Pridať na zoznam",
  wait_btn_loading: "Posielam...",
  wait_success: "Hotovo! Si na zozname. Dáme vedieť prvý.",
  wait_err: "Skús znova, prosím.",
  wait_err_email: "Neplatný email",
  wait_count: "{n} ďalších už čaká",
  wait_close: "Zavrieť",
  tools: {
    "backlink-checker": {
      name: "Backlink Checker",
      desc: "Vlastný AI agent — Common Crawl + Bing + OpenPageRank. Žiadny Ahrefs.",
      tag: "UNIKÁTNE",
    },
    "ai-visibility-checker": {
      name: "AI Visibility Checker",
      desc: "Si viditeľný v ChatGPT, Perplexity, Gemini? Toto NEMÁ žiadny iný tool.",
      tag: "SVETOVÝ UNIKÁT",
    },
    "seo-grade": { name: "SEO Grade Check", desc: "Rýchly A+/D skóring tvojho webu.", tag: "RÝCHLE" },
    "competitor-analyzer": { name: "Konkurenčný analyzátor", desc: "Porovnanie s 5 konkurentmi.", tag: "" },
    "geo-audit": { name: "GEO/Lokálny audit", desc: "NAP konzistencia, lokálne signály.", tag: "" },
    "page-speed": { name: "Page Speed", desc: "Core Web Vitals + odporúčania.", tag: "" },
  },
};

const HUB_EN = {
  hero_eyebrow: "FREE AI TOOLS",
  hero_h1: "6 AI tools that replace a €3 000/mo marketing agency",
  hero_sub: "Our own AI agents. No Ahrefs, no SEMrush. Scan in 30 seconds. No signup.",
  cta_run: "Run",
  card_status_live: "LIVE",
  card_status_soon: "SOON",
  wait_cta: "Notify me when it launches",
  wait_title: "Notify me when we launch",
  wait_sub: "Drop your email and we'll let you know the day it goes live. No spam.",
  wait_placeholder: "you@email.com",
  wait_btn: "Join waitlist",
  wait_btn_loading: "Submitting...",
  wait_success: "Done! You're on the list. We'll email you first.",
  wait_err: "Please try again.",
  wait_err_email: "Invalid email",
  wait_count: "{n} others already waiting",
  wait_close: "Close",
  tools: {
    "backlink-checker": {
      name: "Backlink Checker",
      desc: "Our own AI agent — Common Crawl + Bing + OpenPageRank. No Ahrefs needed.",
      tag: "UNIQUE",
    },
    "ai-visibility-checker": {
      name: "AI Visibility Checker",
      desc: "Are you visible in ChatGPT, Perplexity, Gemini? No other tool has this.",
      tag: "WORLD FIRST",
    },
    "seo-grade": { name: "SEO Grade Check", desc: "Fast A+/D scoring of your site.", tag: "FAST" },
    "competitor-analyzer": { name: "Competitor Analyzer", desc: "Compare with 5 rivals.", tag: "" },
    "geo-audit": { name: "GEO / Local Audit", desc: "NAP consistency, local signals.", tag: "" },
    "page-speed": { name: "Page Speed", desc: "Core Web Vitals + recommendations.", tag: "" },
  },
};

const TOOL_SK = {
  "backlink-checker": {
    title: "Bezplatný Backlink Checker",
    eyebrow: "VLASTNÝ AI AGENT · BEZ AHREFS",
    h1: "Koľko backlinkov má tvoj web?",
    sub: "Sken za 30 sekúnd. Domain Authority + Top zdroje. Bez Ahrefs ($500/mes), SEMrush ($200/mes) alebo akéhokoľvek iného platneho tool-u. Naša AI to robí zadarmo.",
    placeholder: "https://tvoj-web.sk",
    btn: "Spustiť bezplatný audit",
    btn_loading: "Skenujem...",
    score_label: "Backlink skóre",
    estimated_label: "≈ backlinkov",
    da_label: "Domain Authority",
    indexed_label: "Indexované stránky",
    mentions_label: "Spomínajúce domény",
    top_label: "Top spomínajúce zdroje",
    email_soft: "Pošli si full audit emailom (voliteľné)",
    email_hard: "Pošli email pre full report",
    email_btn: "Poslať full report",
    upgrade_msg: "Chceš týždenné monitorovanie + AI link-building odporúčania?",
    upgrade_btn: "Odomknúť za €49/mes",
    privacy: "Žiadny spam. GDPR-friendly.",
    err_invalid: "Neplatná URL",
    err_failed: "Skenovanie zlyhalo. Skús znova.",
  },
  "ai-visibility-checker": {
    title: "AI Visibility Checker",
    eyebrow: "SVETOVÝ UNIKÁT · CHATGPT + PERPLEXITY + GEMINI",
    h1: "Vidia ťa AI vyhľadávače?",
    sub: "70 % nákupných rozhodnutí v 2026 začne v ChatGPT, Perplexity alebo Gemini. Ak ťa AI nevidí, neexistuješ. Toto je jediný tool ktorý ti to zmeria zadarmo.",
    placeholder: "https://tvoj-web.sk",
    btn: "Skenovať AI viditeľnosť",
    btn_loading: "Skenujem...",
    score_label: "AI Visibility skóre",
    chatgpt_label: "ChatGPT",
    perplexity_label: "Perplexity",
    gemini_label: "Gemini",
    voice_label: "Hlasové vyhľadávanie",
    schema_label: "Schema markup",
    content_label: "Kvalita obsahu",
    email_soft: "Pošli si plný report emailom (voliteľné)",
    email_hard: "Pošli email pre full breakdown",
    email_btn: "Poslať plný report",
    upgrade_msg: "Chceš porovnanie s konkurentmi + týždenné re-skeny?",
    upgrade_btn: "Odomknúť za €49/mes",
    privacy: "Žiadny spam. GDPR-friendly.",
    err_invalid: "Neplatná URL",
    err_failed: "Skenovanie zlyhalo. Skús znova.",
  },
};

const TOOL_EN = {
  "backlink-checker": {
    title: "Free Backlink Checker",
    eyebrow: "OUR OWN AI AGENT · NO AHREFS",
    h1: "How many backlinks does your site have?",
    sub: "Scan in 30 seconds. Domain Authority + Top sources. No Ahrefs ($500/mo), no SEMrush ($200/mo), no paid tool of any kind. Our AI does it for free.",
    placeholder: "https://your-site.com",
    btn: "Run free audit",
    btn_loading: "Scanning...",
    score_label: "Backlink score",
    estimated_label: "≈ backlinks",
    da_label: "Domain Authority",
    indexed_label: "Indexed pages",
    mentions_label: "Mentioning domains",
    top_label: "Top mentioning sources",
    email_soft: "Email me the full audit (optional)",
    email_hard: "Email required for full report",
    email_btn: "Send full report",
    upgrade_msg: "Want weekly monitoring + AI link-building suggestions?",
    upgrade_btn: "Unlock for €49/mo",
    privacy: "No spam. GDPR-friendly.",
    err_invalid: "Invalid URL",
    err_failed: "Scan failed. Try again.",
  },
  "ai-visibility-checker": {
    title: "AI Visibility Checker",
    eyebrow: "WORLD FIRST · CHATGPT + PERPLEXITY + GEMINI",
    h1: "Can AI search engines see you?",
    sub: "70% of buying decisions in 2026 start in ChatGPT, Perplexity, or Gemini. If AI can't see you, you don't exist. The only tool that measures this — free.",
    placeholder: "https://your-site.com",
    btn: "Scan AI visibility",
    btn_loading: "Scanning...",
    score_label: "AI Visibility score",
    chatgpt_label: "ChatGPT",
    perplexity_label: "Perplexity",
    gemini_label: "Gemini",
    voice_label: "Voice search",
    schema_label: "Schema markup",
    content_label: "Content quality",
    email_soft: "Email me the full report (optional)",
    email_hard: "Email required for full breakdown",
    email_btn: "Send full report",
    upgrade_msg: "Want competitor comparison + weekly re-scans?",
    upgrade_btn: "Unlock for €49/mo",
    privacy: "No spam. GDPR-friendly.",
    err_invalid: "Invalid URL",
    err_failed: "Scan failed. Try again.",
  },
};

export const HUB_TRANSLATIONS = { sk: HUB_SK, cs: HUB_SK, en: HUB_EN };
export const TOOL_TRANSLATIONS = { sk: TOOL_SK, cs: TOOL_SK, en: TOOL_EN };

export function getHubT(lang) {
  return HUB_TRANSLATIONS[lang] || HUB_TRANSLATIONS.en;
}
export function getToolT(lang, slug) {
  const bag = TOOL_TRANSLATIONS[lang] || TOOL_TRANSLATIONS.en;
  return bag[slug] || TOOL_TRANSLATIONS.en[slug];
}
